import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar, X } from 'lucide-react';
import './DualCalendar.css';


const DualCalendar = ({ onEventSelect }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [islamicMonthInfo, setIslamicMonthInfo] = useState({ month: '', year: '' });
  const [islamicDates, setIslamicDates] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Fetch events from API
  useEffect(() => {
    const fetchEvents = async () => {
      // setLoading(true);
      try {
        const response = await fetch("https://ecom.cyberathon.com/index.php/Api/list_calender");
        const data = await response.json();
        const apiEvents = data.events || [];
        setEvents(apiEvents);
      } catch (error) {
        console.error("Error fetching calendar events:", error);
      }
      // setLoading(false);
    };

    fetchEvents();
  }, []);

  // Fetch Islamic month info
  useEffect(() => {
    const fetchIslamicMonth = async () => {
      try {
        const day = 15;
        const month = currentDate.getMonth() + 1;
        const year = currentDate.getFullYear();
        
        const response = await fetch(
          `https://api.aladhan.com/v1/gToH/${day}-${month}-${year}`
        );
        const data = await response.json();
        
        if (data.code === 200) {
          const hijri = data.data.hijri;
          setIslamicMonthInfo({
            month: hijri.month.en,
            year: hijri.year
          });
        }
      } catch (error) {
        console.error('Error fetching Islamic date:', error);
      }
    };

    fetchIslamicMonth();
    fetchIslamicDatesForMonth();
  }, [currentDate]);

  // Fetch Islamic dates for entire month
  const fetchIslamicDatesForMonth = async () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const month = currentDate.getMonth() + 1;
    const year = currentDate.getFullYear();
    const dates = {};

    try {
      for (let day = 1; day <= daysInMonth; day++) {
        const response = await fetch(
          `https://api.aladhan.com/v1/gToH/${day}-${month}-${year}`
        );
        const data = await response.json();
        
        if (data.code === 200) {
          const dateKey = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          dates[dateKey] = data.data.hijri.day;
        }
      }
      setIslamicDates(dates);
    } catch (error) {
      console.error('Error fetching Islamic dates:', error);
    }
  };

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  // Generate calendar with 6 fixed rows
  const generateCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const totalCells = 42;
    const calendar = [];

    let dayCounter = 1;
    let nextMonthDay = 1;

    for (let i = 0; i < totalCells; i++) {
      if (i < firstDay) {
        calendar.push({ day: null, type: 'prev' });
      } else if (dayCounter <= daysInMonth) {
        calendar.push({ day: dayCounter, type: 'current' });
        dayCounter++;
      } else {
        calendar.push({ day: nextMonthDay, type: 'next' });
        nextMonthDay++;
      }
    }

    const weeks = [];
    for (let i = 0; i < calendar.length; i += 7) {
      weeks.push(calendar.slice(i, i + 7));
    }

    return weeks;
  };

  const getEventsForDate = (day) => {
    if (!day) return [];
    
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(event => event.gregorian_date === dateStr);
  };

  const getIslamicDateForDay = (day) => {
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return islamicDates[dateStr] || '';
  };

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // const handleDateClick = (day, dayEvents) => {
  //   if (dayEvents.length > 0) {
  //     setSelectedEvent(dayEvents[0]);
  //   }
  // };
// handleDateClick function change karo:
const handleDateClick = (day, dayEvents) => {
  if (dayEvents.length > 0) {
    if (onEventSelect) {
      onEventSelect(dayEvents[0]); // ✅ Topbar ko bhejo
    } else {
      setSelectedEvent(dayEvents[0]); // fallback
    }
  }
};
  const weeks = generateCalendar();
  const today = new Date();
  const isCurrentMonth = currentDate.getMonth() === today.getMonth() && 
                         currentDate.getFullYear() === today.getFullYear();

  return (
    <div className="dual-calendar-wrapper">
      <div className="dual-calendar-container">
        {/* Header */}
        <div className="dual-calendar-header">
          <div className="header-content">
            <div className="header-title">
              <h1 className="gregorian-title">
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h1>
              <p className="islamic-subtitle">
                {islamicMonthInfo.month} {islamicMonthInfo.year}
              </p>
            </div>
            <div className="header-controls">
              <button className="nav-btn" onClick={goToPreviousMonth}>
                <ChevronLeft size={24} />
              </button>
              <button className="today-btn" onClick={goToToday}>
                Today
              </button>
              <button className="nav-btn" onClick={goToNextMonth}>
                <ChevronRight size={24} />
              </button>
            </div>
          </div>
          {/* {loading && <div className="loading-indicator">Loading events...</div>} */}
        </div>

        {/* Calendar Body */}
        <div className="dual-calendar-body">
          {/* Day Headers */}
          <div className="day-headers">
            {dayNames.map(day => (
              <div key={day} className="day-header">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="calendar-weeks">
            {weeks.map((week, weekIdx) => (
              <div key={weekIdx} className="calendar-week">
                {week.map((cell, dayIdx) => {
                  const { day, type } = cell;
                  const isToday = type === 'current' && 
                    day === today.getDate() && 
                    isCurrentMonth;
                  
                  const dayEvents = type === 'current' ? getEventsForDate(day) : [];
                  const hasEvents = dayEvents.length > 0;
                  const islamicDay = type === 'current' ? getIslamicDateForDay(day) : '';
                  
                  return (
                    <div
                      key={dayIdx}
                      className={`
                        calendar-cell
                        ${type !== 'current' ? 'inactive' : ''}
                        ${isToday ? 'today' : ''}
                        ${hasEvents ? 'has-events' : ''}
                      `}
                      onClick={() => type === 'current' && handleDateClick(day, dayEvents)}
                    >
                      {day && (
                        <>
                          <div className="cell-header">
                            <div className="gregorian-date">{day}</div>
                            {islamicDay && (
                              <div className="islamic-date">{islamicDay}</div>
                            )}
                          </div>
                          
                          {hasEvents && (
                            <div className="events-container">
                              {dayEvents.slice(0, 2).map((event, idx) => (
                                <div
                                  key={idx}
                                  className="event-badge"
                                  title={event.title}
                                >
                                  {event.title}
                                </div>
                              ))}
                              {dayEvents.length > 2 && (
                                <div className="more-events">
                                  +{dayEvents.length - 2} more
                                </div>
                              )}
                            </div>
                          )}

                          {isToday && (
                            <div className="today-indicator"></div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="dual-calendar-footer">
          <div className="footer-info">
            <Calendar size={16} />
            <span>Total Events: {events.length}</span>
          </div>
          <div className="footer-hint">
            Click on any date to view events
          </div>
        </div>
      </div>

      {/* Event Modal */}
      {/* {selectedEvent && (
        <div className="modal-overlay" onClick={() => setSelectedEvent(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{selectedEvent.title}</h3>
              <button className="modal-close" onClick={() => setSelectedEvent(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="modal-field">
                <div className="modal-label">Hijri Date</div>
                <div className="modal-value">{selectedEvent.hijri_date}</div>
              </div>
              <div className="modal-field">
                <div className="modal-label">Description</div>
                <div className="modal-value">{selectedEvent.description}</div>
              </div>
              <div className="modal-field">
                <div className="modal-label">Gregorian Date</div>
                <div className="modal-value">{selectedEvent.gregorian_date}</div>
              </div>
            </div>
            <button className="modal-button bg-col" onClick={() => setSelectedEvent(null)}>
              Close
            </button>
          </div>
        </div>
      )} */}
    </div>
  );
};

export default DualCalendar;